/**
 * ErrorUtils.js
 * Utilities for consistent error handling across the BattleRoom components
 */

// Error severities
export const ERROR_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
};

// Error categories
export const ERROR_CATEGORY = {
  MAP: 'map',
  TOKEN: 'token',
  GRID: 'grid',
  ASSET: 'asset',
  SESSION: 'session',
  NETWORK: 'network',
  GENERAL: 'general'
};

/**
 * Format an error object for consistent reporting
 * @param {string} message - Error message
 * @param {string} category - Category from ERROR_CATEGORY
 * @param {string} severity - Severity from ERROR_SEVERITY
 * @param {Error|null} originalError - Original error object if available
 * @returns {Object} Formatted error object
 */
export const formatError = (
  message, 
  category = ERROR_CATEGORY.GENERAL, 
  severity = ERROR_SEVERITY.ERROR, 
  originalError = null
) => {
  return {
    message,
    category,
    severity,
    timestamp: new Date().toISOString(),
    originalError: originalError ? {
      message: originalError.message,
      stack: originalError.stack
    } : null
  };
};

/**
 * Handle an error - logs, reports and optionally shows to user
 * @param {Object|Error} error - Error to handle
 * @param {boolean} showToUser - Whether to display to user
 * @param {Function} logger - Logging function (console.error by default)
 */
export const handleError = (error, showToUser = false, logger = console.error) => {
  // Ensure error is in the expected format
  const formattedError = error instanceof Error 
    ? formatError(error.message, ERROR_CATEGORY.GENERAL, ERROR_SEVERITY.ERROR, error)
    : error;
  
  // Log the error
  logger('BattleRoom Error:', formattedError);
  
  // Show to user if needed
  if (showToUser) {
    if (formattedError.severity === ERROR_SEVERITY.CRITICAL) {
      alert(`Critical Error: ${formattedError.message}`);
    } else if (formattedError.severity === ERROR_SEVERITY.ERROR) {
      console.error(`Error: ${formattedError.message}`);
    } else if (formattedError.severity === ERROR_SEVERITY.WARNING) {
      console.warn(`Warning: ${formattedError.message}`);
    } else {
      console.info(`Info: ${formattedError.message}`);
    }
  }
  
  return formattedError;
};

/**
 * Try to safely execute a function with error handling
 * @param {Function} fn - Function to execute
 * @param {Array} args - Arguments to pass to the function
 * @param {string} errorMessage - Error message if function fails
 * @param {Object} options - Additional options
 * @returns {[any, Object|null]} - [result, error]
 */
export const tryCatch = (
  fn, 
  args = [], 
  errorMessage = 'Operation failed', 
  options = {}
) => {
  const { 
    category = ERROR_CATEGORY.GENERAL,
    severity = ERROR_SEVERITY.ERROR,
    showToUser = false,
    defaultReturn = null
  } = options;
  
  try {
    const result = fn(...args);
    return [result, null];
  } catch (error) {
    const formattedError = handleError(
      formatError(errorMessage, category, severity, error),
      showToUser
    );
    return [defaultReturn, formattedError];
  }
};

/**
 * Create a safe wrapper around a function that catches errors
 * @param {Function} fn - Function to wrap
 * @param {string} errorMessage - Error message if function fails
 * @param {Object} options - Additional options
 * @returns {Function} - Safe function that won't throw
 */
export const createSafeFunction = (
  fn,
  errorMessage = 'Operation failed',
  options = {}
) => {
  return (...args) => {
    const [result] = tryCatch(fn, args, errorMessage, options);
    return result;
  };
};
